package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.width
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewCount
import br.com.vvrefeicoes.portal.domain.model.RmpReportKind
import com.composables.icons.lucide.Building2
import com.composables.icons.lucide.Download
import com.composables.icons.lucide.FileText
import com.composables.icons.lucide.Hourglass
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.X
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rmp_export
import portalvv.shared.generated.resources.rmp_exporting
import portalvv.shared.generated.resources.rmp_filter_all_stages
import portalvv.shared.generated.resources.rmp_filter_all_types
import portalvv.shared.generated.resources.rmp_filter_all_units
import portalvv.shared.generated.resources.rmp_filter_clear
import portalvv.shared.generated.resources.rmp_filter_from
import portalvv.shared.generated.resources.rmp_filter_to
import portalvv.shared.generated.resources.rmp_new_date_placeholder
import portalvv.shared.generated.resources.rmp_report_admission
import portalvv.shared.generated.resources.rmp_report_csv
import portalvv.shared.generated.resources.rmp_report_general
import portalvv.shared.generated.resources.rmp_report_print
import portalvv.shared.generated.resources.rmp_report_role_change
import portalvv.shared.generated.resources.rmp_report_transfer
import portalvv.shared.generated.resources.rmp_totals_status
import portalvv.shared.generated.resources.rmp_totals_type

/** "Pendente 3 · Concluído 2". */
internal fun totalsLine(counts: List<RmpOverviewCount>): String = counts.joinToString(" · ") { "${it.label} ${it.total}" }

/** Data digitada (dd/mm/aaaa) como a API recebe (aaaa-mm-dd); incompleta não filtra. */
internal fun filterDate(text: String): String? = displayToIsoDate(text).takeIf { it.length == 10 && it[4] == '-' }

/**
 * Filtros da lista (tipo, unidade, etapa e período de criação) e os totais por status e tipo do que está filtrado,
 * como o dashboard do sistema antigo. As opções vêm da própria lista do usuário.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun RmpFiltersBar(controller: RmpController) {
    val filters = controller.filters
    val totals = controller.totals
    var from by remember { mutableStateOf(filters.from?.let(::isoToDisplayDate).orEmpty()) }
    var to by remember { mutableStateOf(filters.to?.let(::isoToDisplayDate).orEmpty()) }
    val allTypes = stringResource(Res.string.rmp_filter_all_types)
    val allUnits = stringResource(Res.string.rmp_filter_all_units)
    val allStages = stringResource(Res.string.rmp_filter_all_stages)
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        SearchableSelect(
            leadingIcon = Lucide.FileText,
            options = listOf(allTypes) + totals.types.map { it.label },
            selected = totals.types.firstOrNull { it.key == filters.type }?.label ?: allTypes,
            onSelect = { label -> controller.updateFilters(filters.copy(type = totals.types.firstOrNull { it.label == label }?.key)) },
            modifier = Modifier.width(220.dp),
        )
        SearchableSelect(
            leadingIcon = Lucide.Building2,
            options = listOf(allUnits) + totals.units,
            selected = filters.unit ?: allUnits,
            onSelect = { unit -> controller.updateFilters(filters.copy(unit = unit.takeIf { it != allUnits })) },
            searchable = true,
            modifier = Modifier.width(240.dp),
        )
        SearchableSelect(
            leadingIcon = Lucide.Hourglass,
            options = listOf(allStages) + totals.stages.map { it.label },
            selected = totals.stages.firstOrNull { it.key == filters.stage }?.label ?: allStages,
            onSelect = { label -> controller.updateFilters(filters.copy(stage = totals.stages.firstOrNull { it.label == label }?.key)) },
            modifier = Modifier.width(220.dp),
        )
        AppTextField(
            value = from,
            onValueChange = { input ->
                from = maskDate(input)
                if (from.isEmpty() || filterDate(from) != null) controller.updateFilters(filters.copy(from = filterDate(from)))
            },
            label = stringResource(Res.string.rmp_filter_from),
            placeholder = stringResource(Res.string.rmp_new_date_placeholder),
            modifier = Modifier.width(150.dp),
        )
        AppTextField(
            value = to,
            onValueChange = { input ->
                to = maskDate(input)
                if (to.isEmpty() || filterDate(to) != null) controller.updateFilters(filters.copy(to = filterDate(to)))
            },
            label = stringResource(Res.string.rmp_filter_to),
            placeholder = stringResource(Res.string.rmp_new_date_placeholder),
            modifier = Modifier.width(150.dp),
        )
        if (filters.active) {
            AppButton(
                text = stringResource(Res.string.rmp_filter_clear),
                onClick = {
                    from = ""
                    to = ""
                    controller.clearFilters()
                },
                variant = PortalButtonVariant.Text,
                leadingIcon = Lucide.X,
            )
        }
    }
    if (totals.byStatus.isNotEmpty()) {
        Text(
            text = stringResource(Res.string.rmp_totals_status, totalsLine(totals.byStatus)),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = stringResource(Res.string.rmp_totals_type, totalsLine(totals.byType)),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

/** "Exportar dados": CSV da lista, as planilhas do sistema antigo e a lista para imprimir em PDF. */
@Composable
internal fun RmpExportMenu(controller: RmpController) {
    var open by remember { mutableStateOf(false) }
    Box {
        AppButton(
            text = stringResource(if (controller.exporting) Res.string.rmp_exporting else Res.string.rmp_export),
            onClick = { open = true },
            enabled = !controller.exporting,
            variant = PortalButtonVariant.Secondary,
            leadingIcon = Lucide.Download,
        )
        DropdownMenu(expanded = open, onDismissRequest = { open = false }) {
            listOf(
                stringResource(Res.string.rmp_report_csv) to { controller.export() },
                stringResource(Res.string.rmp_report_general) to { controller.exportReport(RmpReportKind.GERAL) },
                stringResource(Res.string.rmp_report_role_change) to { controller.exportReport(RmpReportKind.ALTERACAO) },
                stringResource(Res.string.rmp_report_transfer) to { controller.exportReport(RmpReportKind.TRANSFERENCIA) },
                stringResource(Res.string.rmp_report_admission) to { controller.exportReport(RmpReportKind.ADMISSAO) },
                stringResource(Res.string.rmp_report_print) to { controller.printList() },
            ).forEach { (label, action) ->
                DropdownMenuItem(
                    text = { Text(label) },
                    onClick = {
                        open = false
                        action()
                    },
                )
            }
        }
    }
}
